/** The staged chip-builder panel of TagConditionInput.
 *
 * Stage machine: tenant → category → tag, with a clickable breadcrumb of the
 * partial spec. The tenant stage is the mandatory TenantPicker plus a local
 * "System-wide" sentinel; picking a concrete tenant routes onto the by-id path
 * (plain Autocompletes over that tenant's categories/tags), the sentinel onto
 * the by-code path (freeSolo Autocompletes over deduped codes/names — Enter
 * commits trimmed free text, and an unknown code simply yields zero
 * suggestions). Committing a chip resets to the tenant stage with the panel
 * open; Backspace on an empty input steps back a stage (at the tenant stage
 * it pops the last committed chip); Escape closes the panel and discards the
 * in-progress partial spec.
 */
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography, useTheme } from '@mui/material';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { canonicalTokenError } from '@/utils/canonicalToken';

import { SYSTEM_WIDE } from './types';
import type {
  CanonicalOption,
  CategoryCodeOption,
  CategoryTagOption,
  TagChip,
  TagConditionFetchers,
  TagNameOption,
  TenantCategoryOption,
} from './types';

const DEBOUNCE_MS = 200;

/** Debounced, abortable option loading — the RemoteAutocomplete pattern
 *  (which itself doesn't fit here: the stages need freeSolo + autofocus). */
function useRemoteOptions<T>(fetch: (q: string, signal: AbortSignal) => Promise<T[]>) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = input.trim();
    const t = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const opts = await fetch(trimmed, ctrl.signal);
        if (!ctrl.signal.aborted) setOptions(opts);
      } catch {
        // aborts / transient network errors — the next keystroke retries
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [input, fetch]);

  return { options, loading, setInput };
}

/** The picked tenant; `label` null = the System-wide sentinel (by-code path).
 *  `display` overrides `label` on the committed chip (null = omit the tenant
 *  label — used by the fixed-tenant path where the tenant is implicit). */
interface PickedTenant {
  id: number;
  label: string | null;
  display?: string | null;
}

type PickedCategory =
  | { kind: 'id'; id: number; code: string; name: string }
  | { kind: 'code'; code: string };

function loadingAdornment(loading: boolean, params: { InputProps: { endAdornment?: unknown } }) {
  return (
    <>
      {loading && <CircularProgress size={16} />}
      {params.InputProps.endAdornment as never}
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage pickers
// ---------------------------------------------------------------------------

function CategoryByTenantPicker({
  tenantId,
  fetchers,
  onPick,
}: {
  tenantId: number;
  fetchers: TagConditionFetchers;
  onPick: (c: TenantCategoryOption) => void;
}) {
  const theme = useTheme();
  const fetch = useCallback(
    (q: string, signal: AbortSignal) => fetchers.fetchTenantCategories(tenantId, q, signal),
    [fetchers, tenantId],
  );
  const { options, loading, setInput } = useRemoteOptions(fetch);
  return (
    <Autocomplete<TenantCategoryOption>
      size="small"
      options={options}
      loading={loading}
      value={null}
      onChange={(_, v) => {
        if (v) onPick(v);
      }}
      onInputChange={(_, v) => setInput(v)}
      // Server-side search is authoritative.
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(c) => `${c.name} (${c.code})`}
      openOnFocus
      slotProps={{ popper: { style: { zIndex: theme.zIndex.modal + 20 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Category"
          placeholder="Pick a category…"
          InputProps={{ ...params.InputProps, endAdornment: loadingAdornment(loading, params) }}
        />
      )}
    />
  );
}

function CategoryByCodePicker({
  fetchers,
  onPick,
}: {
  fetchers: TagConditionFetchers;
  onPick: (code: string) => void;
}) {
  const theme = useTheme();
  const fetch = useCallback(
    (q: string, signal: AbortSignal) => fetchers.fetchAllCategoryCodes(q, signal),
    [fetchers],
  );
  const { options, loading, setInput } = useRemoteOptions(fetch);
  return (
    <Autocomplete<CategoryCodeOption, false, false, true>
      freeSolo
      size="small"
      options={options}
      loading={loading}
      value={null}
      onChange={(_, v) => {
        const code = (typeof v === 'string' ? v : v?.code)?.trim();
        if (code) onPick(code);
      }}
      onInputChange={(_, v) => setInput(v)}
      filterOptions={(opts) => opts}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.code)}
      openOnFocus
      slotProps={{ popper: { style: { zIndex: theme.zIndex.modal + 20 } } }}
      renderOption={(props, o) => {
        const { key, ...optionProps } = props as { key?: string } & typeof props;
        return (
          <Box component="li" key={key ?? o.code} {...optionProps} sx={{ fontSize: 13, gap: 1 }}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {o.code}
            </Box>
            {o.name && (
              <Typography variant="caption" color="text.secondary">
                {o.name}
              </Typography>
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Category code"
          placeholder="Pick or type a code…"
          InputProps={{ ...params.InputProps, endAdornment: loadingAdornment(loading, params) }}
        />
      )}
    />
  );
}

function TagByCategoryPicker({
  categoryId,
  fetchers,
  onPick,
}: {
  categoryId: number;
  fetchers: TagConditionFetchers;
  onPick: (t: CategoryTagOption) => void;
}) {
  const theme = useTheme();
  const fetch = useCallback(
    (q: string, signal: AbortSignal) => fetchers.fetchCategoryTags(categoryId, q, signal),
    [fetchers, categoryId],
  );
  const { options, loading, setInput } = useRemoteOptions(fetch);
  return (
    <Autocomplete<CategoryTagOption>
      size="small"
      options={options}
      loading={loading}
      value={null}
      onChange={(_, v) => {
        if (v) onPick(v);
      }}
      onInputChange={(_, v) => setInput(v)}
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(t) => t.name}
      openOnFocus
      slotProps={{ popper: { style: { zIndex: theme.zIndex.modal + 20 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Tag"
          placeholder="Pick a tag…"
          InputProps={{ ...params.InputProps, endAdornment: loadingAdornment(loading, params) }}
        />
      )}
    />
  );
}

function TagByNamePicker({
  categoryCode,
  fetchers,
  onPick,
}: {
  categoryCode: string;
  fetchers: TagConditionFetchers;
  onPick: (name: string) => void;
}) {
  const theme = useTheme();
  const fetch = useCallback(
    (q: string, signal: AbortSignal) => fetchers.fetchTagsForCode(categoryCode, q, signal),
    [fetchers, categoryCode],
  );
  const { options, loading, setInput } = useRemoteOptions(fetch);
  return (
    <Autocomplete<TagNameOption, false, false, true>
      freeSolo
      size="small"
      options={options}
      loading={loading}
      value={null}
      onChange={(_, v) => {
        const name = (typeof v === 'string' ? v : v?.name)?.trim();
        if (name) onPick(name);
      }}
      onInputChange={(_, v) => setInput(v)}
      filterOptions={(opts) => opts}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
      openOnFocus
      slotProps={{ popper: { style: { zIndex: theme.zIndex.modal + 20 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Tag name"
          placeholder="Pick or type a tag name…"
          InputProps={{ ...params.InputProps, endAdornment: loadingAdornment(loading, params) }}
        />
      )}
    />
  );
}

function CanonicalPicker({
  categoryCode,
  fetchers,
  onPick,
}: {
  categoryCode: string;
  fetchers: TagConditionFetchers;
  onPick: (canonical: string) => void;
}) {
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const fetch = useCallback(
    (q: string, signal: AbortSignal) =>
      fetchers.fetchCanonicalsForCode
        ? fetchers.fetchCanonicalsForCode(categoryCode, q, signal)
        : Promise.resolve([]),
    [fetchers, categoryCode],
  );
  const { options, loading, setInput } = useRemoteOptions(fetch);
  return (
    <Autocomplete<CanonicalOption, false, false, true>
      freeSolo
      size="small"
      options={options}
      loading={loading}
      value={null}
      onChange={(_, v) => {
        const raw = (typeof v === 'string' ? v : v?.canonical)?.trim();
        if (!raw) return;
        // Free text reaches the backend either way, so validate before
        // committing: a malformed token is not a typo the user will see, it is
        // a chip that silently resolves to nothing.
        const problem = canonicalTokenError(raw);
        if (problem) {
          setError(problem);
          return;
        }
        setError(null);
        onPick(raw.toLowerCase());
      }}
      onInputChange={(_, v) => {
        setInput(v);
        if (error) setError(null);
      }}
      filterOptions={(opts) => opts}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.canonical)}
      openOnFocus
      slotProps={{ popper: { style: { zIndex: theme.zIndex.modal + 20 } } }}
      renderOption={(props, o) => {
        const { key, ...optionProps } = props as { key?: string } & typeof props;
        return (
          <Box
            component="li"
            key={key ?? o.canonical}
            {...optionProps}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              ~{o.canonical}
            </Box>
            {o.alias_count != null && (
              <Typography variant="caption" color="text.secondary">
                {o.alias_count} alias{o.alias_count === 1 ? '' : 'es'}
              </Typography>
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Canonical"
          placeholder="Pick or type a canonical token…"
          error={Boolean(error)}
          helperText={error ?? undefined}
          InputProps={{ ...params.InputProps, endAdornment: loadingAdornment(loading, params) }}
        />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

interface Props {
  fetchers: TagConditionFetchers;
  /** Append a finished chip (the host dedups); the panel resets to tenant. */
  onCommit: (chip: TagChip) => void;
  /** Close the panel (Escape) — the partial spec is discarded by unmount. */
  onClose: () => void;
  /** Backspace on the empty tenant-stage input: pop the last committed chip. */
  onBackspaceAtRoot: () => void;
  /** Pin the concrete-tag path to one tenant (a tenant-scoped editor): the
   *  tenant stage collapses to a quick pick between "This tenant" and
   *  "System-wide (by code)", the panel starts at the category stage, and
   *  pinned chips omit the tenant label (it's implicit for the editor). */
  fixedTenant?: { id: number };
}

export function SpecBuilderPanel({ fetchers, onCommit, onClose, onBackspaceAtRoot, fixedTenant }: Props) {
  const theme = useTheme();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const fixedPick: PickedTenant | null = fixedTenant
    ? { id: fixedTenant.id, label: 'this tenant', display: null }
    : null;
  const [tenant, setTenant] = useState<PickedTenant | null>(fixedPick);
  const [category, setCategory] = useState<PickedCategory | null>(null);
  // Only meaningful on the by-code path's final stage. A host that supplies no
  // canonical fetcher never sees the switch — that is how the notification
  // `tag` block, a reference to ONE tag, opts out of a shape matching many.
  const [byCanonical, setByCanonical] = useState(false);
  const canonicalOffered = Boolean(fetchers.fetchCanonicalsForCode);

  const stage: 'tenant' | 'category' | 'tag' =
    tenant === null ? 'tenant' : category === null ? 'category' : 'tag';

  // Keep keyboard flow across stages: focus the current stage's input.
  useEffect(() => {
    rootRef.current?.querySelector('input')?.focus();
  }, [stage]);

  /** After a commit: back to the natural starting stage for the next chip. */
  const resetToTenant = () => {
    setCategory(null);
    setByCanonical(false);
    setTenant(fixedPick);
  };

  /** Deliberate step back to the tenant stage (crumb click / Backspace). */
  const stepToTenantStage = () => {
    setCategory(null);
    setByCanonical(false);
    setTenant(null);
  };

  const commitById = (tag: CategoryTagOption) => {
    const cat = category as Extract<PickedCategory, { kind: 'id' }>;
    onCommit({
      spec: { tag_id: tag.id },
      labels: {
        tenant: tenant!.display === undefined ? tenant!.label : tenant!.display,
        category: cat.name,
        tag: tag.name,
      },
    });
    resetToTenant();
  };

  const commitByCode = (tagName: string) => {
    const cat = category as Extract<PickedCategory, { kind: 'code' }>;
    onCommit({
      spec: { category_code: cat.code, tag_name: tagName },
      labels: { tenant: null, category: cat.code, tag: tagName },
    });
    resetToTenant();
  };

  const commitByCanonical = (canonical: string) => {
    const cat = category as Extract<PickedCategory, { kind: 'code' }>;
    onCommit({
      spec: { category_code: cat.code, canonical },
      // `~` is the sigil this shape carries everywhere else it appears.
      labels: { tenant: null, category: cat.code, tag: `~${canonical}` },
    });
    resetToTenant();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Close only the panel — never the surrounding Dialog. (An open
      // Autocomplete popup stops its own Escape before it reaches us.)
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === 'Backspace') {
      const t = e.target as HTMLInputElement;
      if (t.tagName === 'INPUT' && t.value === '') {
        if (stage === 'tag') setCategory(null);
        else if (stage === 'category') setTenant(null);
        else onBackspaceAtRoot();
      }
    }
  };

  const stageHint =
    stage === 'tenant'
      ? fixedTenant
        ? 'This tenant’s own tags — or System-wide to match by category code + name.'
        : 'Pick a tenant — or System-wide to match by category code + name.'
      : stage === 'category'
        ? tenant!.label === null
          ? 'Pick or type a category code.'
          : 'Pick one of the tenant’s categories.'
        : tenant!.label === null
          ? byCanonical
            ? 'Pick or type a canonical token — it matches every name aliased to it.'
            : 'Pick or type the tag name (matched per tenant at render time).'
          : 'Pick the tag.';

  return (
    <Box ref={rootRef} onKeyDown={handleKeyDown} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Breadcrumb of the partial spec — click a crumb to step back to it. */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        {tenant !== null && (
          <>
            <Chip
              size="small"
              label={tenant.display === null ? 'This tenant' : (tenant.label ?? 'System-wide')}
              onClick={stepToTenantStage}
              sx={tenant.label === null ? { fontStyle: 'italic' } : undefined}
            />
            <Box component="span" sx={{ color: 'text.disabled' }}>
              ›
            </Box>
          </>
        )}
        {category !== null && (
          <>
            <Chip
              size="small"
              label={category.kind === 'id' ? `${category.name} (${category.code})` : category.code}
              onClick={() => setCategory(null)}
              sx={category.kind === 'code' ? { fontFamily: 'monospace' } : undefined}
            />
            <Box component="span" sx={{ color: 'text.disabled' }}>
              ›
            </Box>
          </>
        )}
        <Typography variant="caption" color="text.secondary">
          {stageHint}
        </Typography>
      </Box>

      {stage === 'tenant' &&
        (fixedTenant ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              size="small"
              label="This tenant"
              color="primary"
              variant="outlined"
              onClick={() => setTenant({ id: fixedTenant.id, label: 'this tenant', display: null })}
            />
            <Chip
              size="small"
              label="System-wide (by code)"
              variant="outlined"
              sx={{ fontStyle: 'italic' }}
              onClick={() => setTenant({ id: SYSTEM_WIDE.id, label: null })}
            />
          </Box>
        ) : (
          <TenantPicker
            value={null}
            onChange={(t) => {
              if (t) setTenant({ id: t.id, label: t.id === SYSTEM_WIDE.id ? null : t.code || t.name });
            }}
            label="Tenant"
            placeholder="Tenant or System-wide"
            extraOptions={[SYSTEM_WIDE]}
            popperZIndex={theme.zIndex.modal + 20}
          />
        ))}
      {stage === 'category' &&
        (tenant!.label === null ? (
          <CategoryByCodePicker fetchers={fetchers} onPick={(code) => setCategory({ kind: 'code', code })} />
        ) : (
          <CategoryByTenantPicker
            tenantId={tenant!.id}
            fetchers={fetchers}
            onPick={(c) => setCategory({ kind: 'id', id: c.id, code: c.code, name: c.name })}
          />
        ))}
      {stage === 'tag' && category!.kind === 'id' && (
        <TagByCategoryPicker categoryId={category!.id} fetchers={fetchers} onPick={commitById} />
      )}
      {stage === 'tag' && category!.kind === 'code' && (
        <>
          {canonicalOffered && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                size="small"
                label="Tag name"
                variant={byCanonical ? 'outlined' : 'filled'}
                color={byCanonical ? 'default' : 'primary'}
                onClick={() => setByCanonical(false)}
              />
              <Chip
                size="small"
                label="Canonical"
                variant={byCanonical ? 'filled' : 'outlined'}
                color={byCanonical ? 'primary' : 'default'}
                onClick={() => setByCanonical(true)}
              />
            </Box>
          )}
          {byCanonical ? (
            <CanonicalPicker
              categoryCode={category!.code}
              fetchers={fetchers}
              onPick={commitByCanonical}
            />
          ) : (
            <TagByNamePicker
              categoryCode={category!.code}
              fetchers={fetchers}
              onPick={commitByCode}
            />
          )}
        </>
      )}
    </Box>
  );
}
